import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.scss';
import { Helmet } from 'react-helmet';

const TermsOfService = () => {
    return (
        <>
            <Helmet>
                <title>Terms of Service | KeyQuest - XR App Analytics Platform</title>
                <meta name="description" content="KeyQuest's terms of service outline the rules and guidelines for using our AI-powered XR app analytics platform for Meta Quest games." />
                <meta name="robots" content="index, follow" />
            </Helmet>
            
            <div className={styles.legalPage}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <Link to="/" className={styles.backLink}>
                            ← Back to Home
                        </Link>
                        <h1 className={styles.title}>Terms of Service</h1>
                        <p className={styles.subtitle}>Last updated: March 18, 2026</p>
                    </div>

                    <div className={styles.content}>
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
                            <p className={styles.text}>
                                By accessing and using KeyQuest ("the Service"), you accept and agree to be bound by these 
                                Terms of Service. If you do not agree to these terms, please do not use our service.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>2. Description of Service</h2>
                            <p className={styles.text}>
                                KeyQuest is an AI-powered analytics platform that provides insights, tracking, and market research 
                                for XR (Extended Reality) applications, particularly Meta Quest games. Our services include:
                            </p>
                            <ul className={styles.list}>
                                <li>App performance tracking and analytics</li>
                                <li>Keyword and ASO optimization tools</li>
                                <li>Market intelligence and competitor analysis</li>
                                <li>AI-powered insights and recommendations</li>
                                <li>Publisher and category analysis</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>3. User Accounts</h2>
                            <h3 className={styles.subsectionTitle}>3.1 Registration</h3>
                            <p className={styles.text}>
                                To use our service, you must create an account and provide accurate, complete information. 
                                You are responsible for maintaining the confidentiality of your account credentials.
                            </p>
                            
                            <h3 className={styles.subsectionTitle}>3.2 Account Responsibilities</h3>
                            <p className={styles.text}>You are responsible for:</p>
                            <ul className={styles.list}>
                                <li>Maintaining the security of your account</li>
                                <li>All activities that occur under your account</li>
                                <li>Notifying us immediately of unauthorized use</li>
                                <li>Providing accurate and up-to-date information</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>4. Acceptable Use</h2>
                            <p className={styles.text}>You agree to use our service only for lawful purposes and in accordance with these terms. 
                            You must not:</p>
                            <ul className={styles.list}>
                                <li>Use the service for any illegal or unauthorized purpose</li>
                                <li>Attempt to gain unauthorized access to our systems</li>
                                <li>Interfere with or disrupt the service or servers</li>
                                <li>Use automated tools to access the service excessively</li>
                                <li>Reproduce, duplicate, or copy our proprietary data</li>
                                <li>Share your account credentials with others</li>
                                <li>Use the service to violate intellectual property rights</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>5. Data and Analytics</h2>
                            <h3 className={styles.subsectionTitle}>5.1 Data Sources</h3>
                            <p className={styles.text}>
                                Our analytics are based on publicly available data from the Meta Quest Store and other 
                                legitimate sources. We do not access private user data from app stores or individual users.
                            </p>
                            
                            <h3 className={styles.subsectionTitle}>5.2 Data Accuracy</h3>
                            <p className={styles.text}>
                                While we strive to provide accurate and up-to-date information, we cannot guarantee 
                                the accuracy, completeness, or timeliness of all data. Market conditions and app store 
                                data may change rapidly.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>6. Intellectual Property</h2>
                            <p className={styles.text}>
                                The service and its original content, features, and functionality are owned by KeyQuest 
                                and are protected by copyright, trademark, and other intellectual property laws.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>7. Subscription Plans and Payment</h2>
                            <h3 className={styles.subsectionTitle}>7.1 Subscription Tiers</h3>
                            <p className={styles.text}>
                                We offer various subscription plans with different features and usage limits. 
                                Detailed information about each plan is available on our pricing page.
                            </p>
                            
                            <h3 className={styles.subsectionTitle}>7.2 Payment Terms</h3>
                            <p className={styles.text}>
                                Subscription fees are charged in advance on a recurring basis (monthly or annually). 
                                You can cancel your subscription at any time, but refunds are not provided for partial months.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>8. Privacy and Data Protection</h2>
                            <p className={styles.text}>
                                Your privacy is important to us. Our collection and use of personal information is 
                                governed by our Privacy Policy, which is incorporated into these Terms of Service by reference.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>9. Service Availability</h2>
                            <p className={styles.text}>
                                We strive to maintain high service availability but cannot guarantee uninterrupted access. 
                                The service may be temporarily unavailable due to maintenance, updates, or technical issues.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>10. Disclaimers</h2>
                            <p className={styles.text}>
                                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. 
                                WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, 
                                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>11. Limitation of Liability</h2>
                            <p className={styles.text}>
                                IN NO EVENT SHALL KEYQUEST, ITS DIRECTORS, EMPLOYEES, PARTNERS, OR SUPPLIERS BE LIABLE 
                                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>12. Indemnification</h2>
                            <p className={styles.text}>
                                You agree to indemnify and hold harmless KeyQuest from any claims, damages, or expenses 
                                arising from your use of the service or violation of these terms.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>13. Termination</h2>
                            <p className={styles.text}>
                                We may terminate or suspend your account immediately, without prior notice or liability, 
                                for any reason, including if you breach the Terms of Service.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>14. Changes to Terms</h2>
                            <p className={styles.text}>
                                We reserve the right to modify these terms at any time. We will notify users of 
                                significant changes via email or by posting a notice on our platform.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>15. Governing Law</h2>
                            <p className={styles.text}>
                                These Terms of Service shall be governed by and construed in accordance with the laws 
                                of the jurisdiction in which KeyQuest operates, without regard to conflict of law provisions.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>16. Contact Information</h2>
                            <p className={styles.text}>
                                If you have questions about these Terms of Service, please contact us:
                            </p>
                            <div className={styles.contactInfo}>
                                <p><strong>Email:</strong> legal@keyquest.io</p>
                                <p><strong>Website:</strong> <a href="https://keyquest.io" className={styles.link}>https://keyquest.io</a></p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TermsOfService;
